import { BrowserModule } from '@angular/platform-browser';
import { APP_INITIALIZER, importProvidersFrom, NgModule } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import environment from '../environments/environment';
import { AppRoutingModule } from './app.routing'; // Custom Route Paths
import { AppComponent } from './app.component';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { SlimLoaderComponent } from './components/shared/slim-loader/slim-loader.component';
import { LoginGuard } from './common/auth/login.guard';
import { BrowserAnimationsModule, provideAnimations } from '@angular/platform-browser/animations';
import { DatedBrowserComponent } from './components/shared/dated-browser/dated-browser.component';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule, HttpClientXsrfModule } from '@angular/common/http';
import { DragulaModule } from 'ng2-dragula';
import { FileUploadModule } from 'ng2-file-upload';
import { ForwardToFormsGuard } from './common/auth/forward-to-forms.guard';
import { SiteIdInterceptor } from './components/shared/http/site-id.interceptor';
import { ContentTypeInterceptor } from './components/shared/http/content-type.interceptor';
import { RefreshTokenInterceptor } from './components/shared/http/refresh-token.interceptor';
import { ApplicationHttpClient, applicationHttpClientFactory } from './components/shared/http/application-http-client';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { PermissionsGuard } from './common/auth/permissions.guard';
import { MaintenanceModeInterceptor } from './components/shared/http/maintenance-mode.interceptor';
import { GoogleTagManagerModule } from 'angular-google-tag-manager';
import { NgxStripeModule } from 'ngx-stripe';
import { TrackingService } from './services/tracking.service';
import { GalleryModule } from 'ng-gallery';
import { RedirectGuard } from './common/auth/redirect.guard';
import { provideEnvironmentNgxMask } from 'ngx-mask';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatMomentDateModule } from '@angular/material-moment-adapter';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatLegacyDialogModule } from '@angular/material/legacy-dialog';
import { RichTextEditorComponent } from './components/shared/rich-text-editor/rich-text-editor.component';

// Chart.js v4 registration
import {
    Chart,
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
    TimeSeriesScale,
    BarController,
    LineController,
    DoughnutController,
    Filler
} from 'chart.js';
import 'chartjs-adapter-moment';

// Register Chart.js components globally
Chart.register(
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    ArcElement,         // ✅ For doughnut/pie charts
    Title,
    Tooltip,
    Legend,
    TimeSeriesScale,
    BarController,      // ✅ Register Bar Controller
    LineController,     // ✅ Register Line Controller
    DoughnutController, // ✅ Register Doughnut Controller
    Filler             // ✅ Required for dataset fill property to work
);
// Note: Global Chart.js font defaults are set in graphing.service.ts

export function appFactory() {
    return (): Promise<any> => {
        // reset tracking on app init
        if (TrackingService.read()) {
            TrackingService.clear();
        }
        return new Promise(((resolve, reject) => resolve(true)));
    };
}

@NgModule({
    declarations: [
        AppComponent,
    ],
    imports: [
        // SharedModule,
        // AppLayoutModulePayhoa,
        BrowserModule,
        BrowserAnimationsModule,
        DatedBrowserComponent,
        // EditorModule,
        RichTextEditorComponent,
        // FormsModule,
        HttpClientModule,
        HttpClientXsrfModule,
        NgbModule,
        MatSnackBarModule,
        // AdminLayoutModule,
        // V2LayoutModule,
        // AppLayoutModule,
        // AuthLayoutModule,
        DragulaModule.forRoot(),
        // ErrorModule,
        SlimLoaderComponent,
        BrowserAnimationsModule,
        ReactiveFormsModule,
        FileUploadModule,
        // TagsComponent,
        NgxStripeModule.forRoot(environment.StripePublishableKey, {
            betas: ['blocked_card_brands_beta_2'],
        }),
        GoogleTagManagerModule.forRoot({
            id: environment.GoogleGtm.container,
        }),

        // Always import app routing last!
        AppRoutingModule,
    ],
    providers: [
        {
            provide: HTTP_INTERCEPTORS,
            useClass: SiteIdInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: MaintenanceModeInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: ContentTypeInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: RefreshTokenInterceptor,
            multi: true,
        },
        {
            provide: ApplicationHttpClient,
            useFactory: applicationHttpClientFactory,
            deps: [
                HttpClient,
                Location,
                Router,
            ],
        },
        {
            provide: APP_INITIALIZER,
            useFactory: appFactory,
            deps: [],
            multi: true,
        },
        LoginGuard,
        ForwardToFormsGuard,
        PermissionsGuard,
        RedirectGuard,
        importProvidersFrom(MatMomentDateModule),
        importProvidersFrom(MatSelectModule),
        importProvidersFrom(MatDatepickerModule),
        importProvidersFrom(MatLegacyDialogModule),
        provideAnimations(),
        provideEnvironmentNgxMask(),
        importProvidersFrom(GalleryModule),
    ],
    bootstrap: [AppComponent],
})
export class AppModule
{
}
